<template>
  <validation-observer ref="form" v-slot="{ invalid, valid }">
    <v-form>
      <v-container>
        <v-row>
          <v-col cols="12" md="12">
            <custom-label title="User Details" />
          </v-col>
        </v-row>
        <!-- Avatar Image Preview -->
        <v-row v-if="!isCreate && isClient && profile.avatar" class="mb-5">
          <v-img :src="avatarImage" max-width="200" max-height="250" min-height="230" />
        </v-row>

        <!-- Role Display -->
        <!-- <v-row v-if="!isCreate && profile.role">
          <v-col cols="2">
            <custom-label item-field-name="Role" />
            {{ profile.role | translateOptions(roleOptions) }}
          </v-col>
        </v-row> -->

        <v-row>
          <!-- Avatar Upload -->
          <v-col cols="12" md="12" v-if="shouldShowAvatarUpload" class="pb-0">
            <custom-label item-field-name="Avatar" />
            <ValidationProvider v-slot="{ errors }" name="avatar">
              <v-file-input
                v-model="profile.newImage"
                class="flex-grow-1"
                flat
                label="Photo Upload"
                outlined
                accept="image/jpeg,image/png"
              />
            </ValidationProvider>
          </v-col>

          <!-- Last Name & First Name Fields (only for rml.member role) -->
          <template v-if="isClient">
            <v-col :cols="12" :md="6" class="pt-0">
              <ValidationProvider v-slot="{ errors }" name="last name" rules="required">
                <FletrixTextField
                  label="*Last Name"
                  v-model="profile.name_last"
                  hide-details
                ></FletrixTextField>
                <span class="redRemind">{{ errors[0] }}</span>
              </ValidationProvider>
            </v-col>

            <v-col :cols="12" :md="6" class="pt-0">
              <ValidationProvider v-slot="{ errors }" name="first name" rules="required">
                <FletrixTextField
                  label="*First Name"
                  v-model="profile.name_first"
                  hide-details
                ></FletrixTextField>
                <span class="redRemind">{{ errors[0] }}</span>
              </ValidationProvider>
            </v-col>

            <v-col cols="12" md="12">
              <FletrixDatePicker
                v-model="profile.birthday"
                label="Birthday"
                :max="new Date().toISOString().split('T')[0]"
                display-date-format="DD/MM/YYYY"
              />
            </v-col>

            <v-col cols="12" md="12">
              <ValidationProvider v-slot="{ errors }" name="email" rules="email">
                <FletrixTextField
                  label="Email"
                  v-model="profile.email"
                  hide-details
                ></FletrixTextField>
                <span class="redRemind">{{ errors[0] }}</span>
              </ValidationProvider>
            </v-col>
          </template>

          <!-- Name Field (for non-member roles) -->
          <v-col cols="12" class="pt-0" v-else>
            <ValidationProvider v-slot="{ errors }" name="name" rules="required">
              <FletrixTextField label="Name" v-model="profile.name" hide-details></FletrixTextField>
              <span class="redRemind">{{ errors[0] }}</span>
            </ValidationProvider>
          </v-col>

          <!-- Phone Validator -->
          <v-col cols="12" md="12">
            <FletrixPhone label="Phone" ref="phoneValidator" v-model="profile.phone" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="12">
            <div>
              <v-btn
                v-if="isLoginAdmin && profile.user && !isCreate"
                color="primary"
                @click="openChangePasswordDialog"
              >
                Change Password
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <!--  Password Fields -->
        <v-row v-if="isLoginAdmin && isCreate">
          <v-col cols="12" md="6">
            <custom-label item-field-name="Password" />
            <ValidationProvider
              v-slot="{ errors }"
              :rules="{ required: true, regex: /^.{5,}$/ }"
              name="new password"
              vid="confirm"
            >
              <FletrixTextField
                v-model="profile.password"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPassword ? 'text' : 'password'"
                @click:append="showPassword = !showPassword"
                solo
                hide-details
                autocomplete="new-password"
              ></FletrixTextField>
              <span class="redRemind">{{ errors[0] }}</span>
            </ValidationProvider>
          </v-col>

          <v-col cols="12" md="6">
            <custom-label item-field-name="Confirm Password" />
            <ValidationProvider
              v-slot="{ errors }"
              name="confirm new password"
              rules="required|confirmed:confirm"
            >
              <FletrixTextField
                v-model="profile.confirmPassword"
                :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showConfirmPassword ? 'text' : 'password'"
                @click:append="showConfirmPassword = !showConfirmPassword"
                solo
                hide-details
              ></FletrixTextField>
              <span class="redRemind">{{ errors[0] }}</span>
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Client Specific Settings -->
        <template v-if="isClient">
          <v-row>
            <v-col cols="12">
              <FletrixTextarea v-model="remarks" hide-details label="Remarks"></FletrixTextarea>
            </v-col>

            <template v-if="!isCreate"> </template>
          </v-row>

          <!-- Patients Section -->
          <v-row v-if="isClient && isCreate">
            <v-col cols="12">
              <custom-label item-field-name="Patients" />
              <PatientsList v-model="profile.patients" @change="onPatientsChange" />
            </v-col>
          </v-row>
        </template>

        <!-- Action Buttons -->
        <slot name="footer">
          <div class="d-flex justify-space-between align-center" style="margin-top: 20px">
            <!-- Create/Update Button (right side) -->
            <div class="ml-auto">
              <v-btn :loading="isSubmitting" color="primary" @click="promptCreate()" v-if="isCreate"
                >Create</v-btn
              >
              <v-btn
                :loading="isSubmitting"
                color="primary"
                @click="promptUpdate()"
                v-if="!isCreate"
                >Update</v-btn
              >
            </div>
          </div>
        </slot>
      </v-container>
    </v-form>

    <!-- Change Password Dialog -->
    <v-dialog v-model="changePasswordDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="primaryDialogTitle">
          <span class="headline">Change Password</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeChangePasswordDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <ValidationObserver ref="passwordForm" v-slot="{ invalid, valid }">
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :rules="{ required: true, regex: /^.{5,}$/ }"
                    name="new password"
                    vid="newPasswordConfirm"
                  >
                    <FletrixTextField
                      v-model="passwordForm.newPw"
                      :error="!!errors.length"
                      :error-messages="errors.length ? 'Must be 5 characters or more' : ''"
                      label="New Password"
                      outlined
                      autocomplete="new-password"
                      :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="showNewPassword ? 'text' : 'password'"
                      @click:append="showNewPassword = !showNewPassword"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="confirm new password"
                    rules="required|confirmed:newPasswordConfirm"
                  >
                    <FletrixTextField
                      v-model="passwordForm.confirmNewPw"
                      :error="!!errors.length"
                      :error-messages="errors"
                      label="Confirm New Password"
                      outlined
                      autocomplete="new-password"
                      :append-icon="showConfirmNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="showConfirmNewPassword ? 'text' : 'password'"
                      @click:append="showConfirmNewPassword = !showConfirmNewPassword"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeChangePasswordDialog">Cancel</v-btn>
            <v-btn
              color="primary"
              :disabled="!valid"
              :loading="changingPassword"
              @click="resetUserPassword"
            >
              Change Password
            </v-btn>
          </v-card-actions>
        </ValidationObserver>
      </v-card>
    </v-dialog>
  </validation-observer>
</template>

<script>
import api from '@/api';
import editDialogMixin from '@/services/editDialogMixin';
import CustomLabel from '../customFormLabel/customLabel';
import { get } from 'lodash';
import moment from 'moment';
import { uploadSingleAttachment } from '@/api/attachment';
import { parsePhoneNum, parseAreaCode } from '@/services/filters';
import { RESTFUL } from '@/data/constants';
import { areaCodeOptions, roleOptions } from '@/data/optionsConstants';
import ApiError from '@/api/ApiError';
import { mapGetters } from 'vuex';
import VeeValidate from 'vee-validate';
import { isValidPhone } from '@/services/validatePhone.js';
import { crudCreate, crudRead, crudUpdate } from '@/api/crud';
import { attnThumbHref } from '@/services/filters';
import PatientsList from '@/components/MemberProfile/PatientsList.vue';
import { getOid } from '@/services/utils';
import { roles } from '@/data/constants';
import { resetMasterPassword } from '@/api/auth/membership';
import { mapActions } from 'vuex';
import { FletrixTextField, FletrixPhone, FletrixTextarea, FletrixSelect, FletrixDatePicker } from '@/components/Atom';

VeeValidate.Validator.extend(
  'phoneValidation',
  {
    getMessage: () => 'Invalid phone number',
    validate: (value, [areaCode]) => {
      return isValidPhone(areaCode, value);
    },
  },
  {
    hasTarget: true,
  },
);

export default {
  name: 'MemberProfileEditForm',
  components: {
    CustomLabel,
    PatientsList,
    FletrixTextField,
    FletrixPhone,
    FletrixTextarea,
    FletrixSelect,
    FletrixDatePicker,
  },
  mixins: [editDialogMixin],
  props: {
    title: {},
    id: {},
    profile: {},
    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      areaCodeOptions: areaCodeOptions,
      showPassword: false,
      showConfirmPassword: false,
      roleOptions,
      latestVisit: '',
      loadingLatestVisit: false,
      needCreateUser: false,
      changePasswordDialog: false,
      changingPassword: false,
      passwordForm: {
        newPw: null,
        confirmNewPw: null,
      },
      showNewPassword: false,
      showConfirmNewPassword: false,
    };
  },
  computed: {
    ...mapGetters('auth', { isLoginAdmin: 'isAdmin' }),
    isAdmin() {
      return this.profile.role === roles.admin;
    },
    isClient() {
      return this.profile.role === roles.member;
    },
    avatarImage() {
      return this.profile?.avatar && attnThumbHref(this.profile?.avatar);
    },
    shouldShowAvatarUpload() {
      return false; //this.isClient && (this.isCreate || !this.profile.avatar);
    },
    remarks: {
      get() {
        return typeof this.profile?.remarks === 'object'
          ? Object.keys(this.profile?.remarks)?.length === 0
            ? ''
            : JSON.stringify(this.profile?.remarks)
          : this.profile?.remarks || '';
      },
      set(value) {
        this.profile.remarks = value;
      },
    },
  },
  async beforeMount() {},
  watch: {
    profile: {
      handler(newVal) {},
    },
  },
  methods: {
    ...mapActions('alert', ['updateAlertMessage']),
    get,
    async promptCreate() {
      if (
        !(await this.$refs.form?.validate?.()) &&
        !(await this.$refs?.phoneValidator?.$refs?.form?.validate?.())
      ) {
        return;
      }
      try {
        let payload;
        if (!this.isClient) {
          payload = {
            role: this.profile.role,
            sid: this.profile?.sid,
            name: this.profile?.name,
            phone: this.profile?.phone,
            isTestingUser: this.profile?.isTestingUser,
            enabled: this.profile.enabled,
          };
        } else {
          payload = {
            role: this.profile.role,
            sid: this.profile?.sid,
            name: `${this.profile?.name_last || ''} ${this.profile?.name_first || ''}`.trim(),
            name_last: this.profile?.name_last,
            name_first: this.profile?.name_first,
            email: this.profile?.email,
            phone: this.profile?.phone,
            isAllowPostQuotationInWeb: this.profile.isAllowPostQuotationInWeb,
            isBannedSellerInWhatsapp: this.profile.isBannedSellerInWhatsapp,
            isBannedCreateQuotationInWhatsapp: this.profile.isBannedCreateQuotationInWhatsapp,
            isBannedCreateLookingInWhatsapp: this.profile.isBannedCreateLookingInWhatsapp,
            bannedReasonInWhatsapp: this.profile.bannedReasonInWhatsapp,
            enabled: this.profile.enabled,
            maximumDailyWhatsappQuota: this.profile.maximumDailyWhatsappQuota,
            maximumMonitorQuota: this.profile.maximumMonitorQuota,
            companyName: this.profile.companyName,
            description: this.profile.description,
            badges: this.profile.badges,
            createdSource: 'manual',
            patients: this.profile.patients || [],
          };
          if (this.profile.birthday) {
            payload.birthday = this.profile.birthday;
          }
        }
        payload.pw = this.profile.password || undefined;

        if (this.profile.newImage) {
          const newImage = await uploadSingleAttachment(api, this.profile.newImage);
          payload = {
            ...payload,
            avatar: newImage._id,
          };
        }
        this.$emit('create', payload);
        this.$refs.form.reset();
        this.areaCode = '';
        return;
      } catch (err) {
        this.handleError(err);
      }
    },
    async promptUpdate() {
      if (
        !(await this.$refs.form?.validate?.()) &&
        !(await this.$refs?.phoneValidator?.$refs?.form?.validate?.())
      ) {
        return;
      }
      try {
        let payload;
        if (!this.isClient) {
          payload = {
            role: this.profile.role,
            sid: this.profile?.sid,
            name: this.profile?.name,
            phone: this.profile?.phone,
            isTestingUser: this.profile?.isTestingUser,
            enabled: this.profile.enabled,
            remarks: this.profile.remarks,
          };
        } else {
          payload = {
            role: this.profile.role,
            sid: this.profile?.sid,
            name: `${this.profile?.name_last || ''} ${this.profile?.name_first || ''}`.trim(),
            name_last: this.profile?.name_last,
            name_first: this.profile?.name_first,
            email: this.profile?.email,
            phone: this.profile?.phone,
            enabled: this.needCreateUser ? this.needCreateUser : this.profile.user.enabled,
            remarks: this.profile.remarks,
            patients: this.profile.patients,
          };
          if (this.profile.birthday) {
            payload.birthday = this.profile.birthday;
          }
          this.needCreateUser = false;
        }
        if (this.profile.newImage) {
          const newImage = await uploadSingleAttachment(api, this.profile.newImage);
          payload = {
            ...payload,
            avatar: newImage._id,
          };
        }
        this.$emit('update', payload);
        return;
      } catch (err) {
        this.handleError(err);
      }
    },
    openChangePasswordDialog() {
      if (!this.profile.user) {
        this.updateAlertMessage({
          msg: 'Cannot get user ID',
          type: 'error',
        });
        return;
      }
      this.changePasswordDialog = true;
      this.passwordForm = {
        newPw: null,
        confirmNewPw: null,
      };
      this.showNewPassword = false;
      this.showConfirmNewPassword = false;
    },

    closeChangePasswordDialog() {
      this.changePasswordDialog = false;
      this.passwordForm = {
        newPw: null,
        confirmNewPw: null,
      };
      this.showNewPassword = false;
      this.showConfirmNewPassword = false;
      this.$refs.passwordForm?.reset();
    },

    async resetUserPassword() {
      try {
        this.changingPassword = true;
        const userId = getOid(this.profile.user);
        await resetMasterPassword(api, userId, this.passwordForm.newPw);

        await this.updateAlertMessage({
          msg: 'Password changed successfully',
          type: 'success',
          color: 'success',
        });

        this.closeChangePasswordDialog();
      } catch (error) {
        console.error('Error changing password:', error);
        await this.updateAlertMessage({
          msg: 'Failed to change password',
          type: 'error',
        });
      } finally {
        this.changingPassword = false;
      }
    },

    deleteLoginHistory(item) {
      this.profile.todayLoginHistory = this.profile.todayLoginHistory.filter(
        entry => entry !== item,
      );
    },
    onPatientsChange() {
      // Handle patients change event
      // The patients array is automatically updated via v-model
    },
  },
};
</script>

<style scoped lang="scss">
.redRemind {
  position: absolute;
  color: red;
  font-size: 12px;
}
</style>
